import {Stack, TextInput, createStyles} from '@mantine/core';
import React, {useState} from 'react';

import QueryingTemplateCollectionSelector from 'notebook/entry-templates/collections/TemplateCollectionSelector';
import notifications from 'notifications';
import PlateDesignTemplateDAO from 'plates/dao/PlateDesignTemplateDAO';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal, {ModalButton} from 'ui-library/modals/Modal';

interface SaveScratchPlateAsDesignTemplateModalProps {
  scratchPlateId: string;
  defaultName: string;
  opened: boolean;
  onClose: () => void;
}

export default function SaveScratchPlateAsDesignTemplateModal({
  scratchPlateId,
  defaultName,
  opened,
  onClose,
}: SaveScratchPlateAsDesignTemplateModalProps): JSX.Element | null {
  const {classes} = useStyles();
  const [designName, setDesignName] = useState(defaultName);
  const [templateCollectionId, setTemplateCollectionId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const designNameTrimmed = designName.trim();
  const canSubmit = designNameTrimmed.length > 0 && templateCollectionId != null;

  function handleClose(): void {
    setTemplateCollectionId(null);
    onClose();
  }

  async function handleSubmit(): Promise<void> {
    if (!canSubmit || isSubmitting || templateCollectionId == null) {
      return;
    }
    setIsSubmitting(true);
    try {
      await PlateDesignTemplateDAO.createPlateDesignTemplate({
        scratchPlateId,
        templateCollectionId,
        name: designNameTrimmed,
      });
      notifications.success(`Successfully created plate design template "${designNameTrimmed}".`);
      handleClose();
    } catch (error) {
      notifications.notifyError(error as Response);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!opened) {
    return null;
  }

  const buttons: Array<ModalButton> = [
    {
      text: 'Cancel',
      cancel: true,
    },
    {
      text: 'Save',
      submit: true,
      disabled: !canSubmit || isSubmitting,
    },
  ];

  return (
    <Modal
      title="Save as template"
      onRequestClose={handleClose}
      onRequestSubmit={handleSubmit}
      buttons={buttons}
      medium={true}
    >
      <Stack spacing="md">
        <TextInput
          data-autofocus={true}
          required={true}
          label="Template name"
          value={designName}
          placeholder="Template name..."
          onChange={(e) => {
            setDesignName(e.currentTarget.value);
          }}
          size="md"
          classNames={{input: classes.input}}
        />
        <div>
          {/* I added a space after "Template collection" so that there will be sufficient spacing between the
          field label and the asterisk, to match the mantine TextInput spacing above. */}
          <FieldLabel isRequired={true} label="Template collection " />
          <QueryingTemplateCollectionSelector
            value={templateCollectionId}
            onChange={(templateCollection) => {
              setTemplateCollectionId(templateCollection.id);
            }}
            searchable={true}
          />
        </div>
      </Stack>
    </Modal>
  );
}

const useStyles = createStyles(() => ({
  input: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    '&::placeholder': {
      fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    },
  },
}));
